'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import JSZip from 'jszip';
import { getToolAuthToken } from '@/lib/browser-tool-usage';
import type { ToolLiveConfig } from '@/lib/tools';

type OutputFormat = 'png' | 'jpeg' | 'webp';
type FrameMode = 'fit' | 'fill';
type BackgroundPreset = 'white' | 'off-white' | 'neutral' | 'warm';
type WatermarkKind = 'off' | 'text' | 'logo';
type QueueStatus = 'queued' | 'processing' | 'done' | 'failed';

type WorkflowPreset = {
  id: string;
  label: string;
  summary: string;
  width: number;
  height: number;
  frameMode: FrameMode;
  backgroundPreset: BackgroundPreset;
  outputFormat: OutputFormat;
  quality: number;
  watermarkKind: WatermarkKind;
  watermarkText: string;
  renamePattern: string;
};

type QueueItem = {
  id: string;
  file: File;
  name: string;
  type: string;
  size: number;
  width: number;
  height: number;
  sourceUrl: string;
  status: QueueStatus;
  outputBlob: Blob | null;
  outputName: string | null;
  outputSize: number | null;
  error: string | null;
};

type Drawable = {
  source: CanvasImageSource;
  width: number;
  height: number;
  dispose: () => void;
};

type BrowserBatchListingAssetOptimizerProps = {
  variant?: ToolLiveConfig;
};

const supportedTypes = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/bmp']);

const workflowPresets: WorkflowPreset[] = [
  {
    id: 'marketplace-square',
    label: 'Marketplace square',
    summary:
      'Square listing exports with clean framing, white fill, and lighter upload-ready JPG output.',
    width: 1600,
    height: 1600,
    frameMode: 'fit',
    backgroundPreset: 'white',
    outputFormat: 'jpeg',
    quality: 0.84,
    watermarkKind: 'off',
    watermarkText: '',
    renamePattern: '{name}-marketplace-{index}',
  },
  {
    id: 'catalog-portrait',
    label: 'Catalog portrait',
    summary:
      'Taller catalog framing for product cards, feature stacks, and cleaner portrait-style listing shots.',
    width: 1400,
    height: 1800,
    frameMode: 'fit',
    backgroundPreset: 'off-white',
    outputFormat: 'jpeg',
    quality: 0.86,
    watermarkKind: 'off',
    watermarkText: '',
    renamePattern: '{name}-catalog-{index}',
  },
  {
    id: 'lightweight-thumbnail',
    label: 'Lightweight thumbnail',
    summary:
      'Smaller thumbnails for quicker storefront uploads, lighter packs, and faster review workflows.',
    width: 1200,
    height: 1200,
    frameMode: 'fit',
    backgroundPreset: 'white',
    outputFormat: 'webp',
    quality: 0.72,
    watermarkKind: 'off',
    watermarkText: '',
    renamePattern: '{name}-thumb-{index}',
  },
  {
    id: 'branded-product-image',
    label: 'Branded product image',
    summary:
      'Clean product framing with a warm catalog background and a light watermark for branded exports.',
    width: 1800,
    height: 1800,
    frameMode: 'fit',
    backgroundPreset: 'warm',
    outputFormat: 'png',
    quality: 0.92,
    watermarkKind: 'text',
    watermarkText: 'Brand',
    renamePattern: '{name}-brand-{index}',
  },
];

const outputOptions: Array<{
  value: OutputFormat;
  label: string;
  mimeType: string;
  extension: string;
  qualityEnabled: boolean;
}> = [
  { value: 'png', label: 'PNG', mimeType: 'image/png', extension: 'png', qualityEnabled: false },
  {
    value: 'jpeg',
    label: 'JPG',
    mimeType: 'image/jpeg',
    extension: 'jpg',
    qualityEnabled: true,
  },
  {
    value: 'webp',
    label: 'WebP',
    mimeType: 'image/webp',
    extension: 'webp',
    qualityEnabled: true,
  },
];

const frameModeOptions: Array<{ value: FrameMode; label: string }> = [
  { value: 'fit', label: 'Fit inside canvas' },
  { value: 'fill', label: 'Fill canvas' },
];

const backgroundPresets: Array<{
  value: BackgroundPreset;
  label: string;
  color: string;
}> = [
  { value: 'white', label: 'White', color: '#ffffff' },
  { value: 'off-white', label: 'Off-white', color: '#f4f1ea' },
  { value: 'neutral', label: 'Neutral', color: '#e8ebef' },
  { value: 'warm', label: 'Warm light', color: '#f5eee3' },
];

const maxBatchFiles = 100;
const maxFileSize = 15 * 1024 * 1024;

function formatFileSize(size: number) {
  if (size < 1024) {
    return `${size} B`;
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }

  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
}

function normalizeSourceLabel(type: string | null) {
  if (!type) {
    return 'Image';
  }

  const label = type.replace('image/', '').toUpperCase();
  return label === 'JPEG' ? 'JPG' : label;
}

function getBaseName(fileName: string) {
  const lastDot = fileName.lastIndexOf('.');
  return lastDot > 0 ? fileName.slice(0, lastDot) : fileName;
}

function sanitizeFileNameSegment(value: string) {
  return value
    .trim()
    .replace(/[<>:"/\\|?*\u0000-\u001F]/g, '-')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

function buildOutputName(
  pattern: string,
  item: QueueItem,
  index: number,
  preset: WorkflowPreset | null,
  extension: string,
) {
  const presetLabel = sanitizeFileNameSegment(preset?.label.toLowerCase() ?? 'listing');
  const baseName = sanitizeFileNameSegment(getBaseName(item.name)) || `asset-${index + 1}`;
  const output = (pattern || '{name}-{preset}-{index}')
    .replaceAll('{name}', baseName)
    .replaceAll('{index}', String(index + 1).padStart(2, '0'))
    .replaceAll('{preset}', presetLabel || 'listing');

  return `${sanitizeFileNameSegment(output) || `asset-${index + 1}`}.${extension}`;
}

function getProgressLabel(status: QueueStatus) {
  switch (status) {
    case 'processing':
      return 'Processing';
    case 'done':
      return 'Ready';
    case 'failed':
      return 'Failed';
    default:
      return 'Queued';
  }
}

async function yieldToBrowser() {
  await new Promise<void>((resolve) => window.setTimeout(resolve, 0));
}

async function loadDrawableFromFile(file: File): Promise<Drawable> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file);
      return {
        source: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        dispose: () => bitmap.close(),
      };
    } catch {
      // Fall back to HTMLImageElement decoding for browsers that reject createImageBitmap.
    }
  }

  const objectUrl = URL.createObjectURL(file);
  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const nextImage = new Image();
    nextImage.onload = () => resolve(nextImage);
    nextImage.onerror = () => reject(new Error('This image could not be decoded in your browser.'));
    nextImage.src = objectUrl;
  });

  return {
    source: image,
    width: image.naturalWidth,
    height: image.naturalHeight,
    dispose: () => URL.revokeObjectURL(objectUrl),
  };
}

function drawWatermark(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  kind: WatermarkKind,
  text: string,
  logoDrawable: Drawable | null,
) {
  if (kind === 'off') {
    return;
  }

  const padding = Math.round(Math.min(width, height) * 0.045);

  if (kind === 'logo' && logoDrawable) {
    const maxWidth = Math.round(width * 0.22);
    const maxHeight = Math.round(height * 0.11);
    const scale = Math.min(maxWidth / logoDrawable.width, maxHeight / logoDrawable.height, 1);
    const drawWidth = Math.max(1, Math.round(logoDrawable.width * scale));
    const drawHeight = Math.max(1, Math.round(logoDrawable.height * scale));

    context.save();
    context.globalAlpha = 0.78;
    context.drawImage(
      logoDrawable.source,
      width - drawWidth - padding,
      height - drawHeight - padding,
      drawWidth,
      drawHeight,
    );
    context.restore();
    return;
  }

  const watermarkText = text.trim() || 'Brand';
  const fontSize = Math.max(26, Math.round(Math.min(width, height) * 0.034));
  const pillHeight = fontSize + 18;
  const pillPaddingX = Math.round(fontSize * 0.7);

  context.save();
  context.font = `700 ${fontSize}px Inter, system-ui, sans-serif`;
  context.textBaseline = 'middle';
  const textWidth = context.measureText(watermarkText).width;
  const pillWidth = Math.round(textWidth + pillPaddingX * 2);
  const pillX = width - pillWidth - padding;
  const pillY = height - pillHeight - padding;

  context.fillStyle = 'rgba(12, 18, 31, 0.56)';
  context.beginPath();
  context.roundRect(pillX, pillY, pillWidth, pillHeight, Math.round(pillHeight / 2));
  context.fill();

  context.fillStyle = 'rgba(255, 255, 255, 0.92)';
  context.fillText(watermarkText, pillX + pillPaddingX, pillY + pillHeight / 2 + 1);
  context.restore();
}

async function prepareQueueItem(file: File): Promise<QueueItem> {
  const drawable = await loadDrawableFromFile(file);

  try {
    return {
      id: crypto.randomUUID(),
      file,
      name: file.name,
      type: file.type,
      size: file.size,
      width: drawable.width,
      height: drawable.height,
      sourceUrl: URL.createObjectURL(file),
      status: 'queued',
      outputBlob: null,
      outputName: null,
      outputSize: null,
      error: null,
    };
  } finally {
    drawable.dispose();
  }
}

async function processQueueItem(
  item: QueueItem,
  index: number,
  preset: WorkflowPreset | null,
  canvasWidth: number,
  canvasHeight: number,
  frameMode: FrameMode,
  backgroundPreset: BackgroundPreset,
  outputFormat: OutputFormat,
  quality: number,
  watermarkKind: WatermarkKind,
  watermarkText: string,
  logoDrawable: Drawable | null,
  renamePattern: string,
) {
  const selectedOutput =
    outputOptions.find((option) => option.value === outputFormat) ?? outputOptions[0];
  const backgroundColor =
    backgroundPresets.find((presetOption) => presetOption.value === backgroundPreset)?.color ??
    '#ffffff';
  const targetWidth = canvasWidth;
  const targetHeight = canvasHeight;

  const drawable = await loadDrawableFromFile(item.file);

  try {
    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const context = canvas.getContext('2d');

    if (!context) {
      throw new Error('Your browser could not prepare this listing image.');
    }

    context.clearRect(0, 0, targetWidth, targetHeight);
    context.fillStyle = backgroundColor;
    context.fillRect(0, 0, targetWidth, targetHeight);

    const scale =
      frameMode === 'fill'
        ? Math.max(targetWidth / drawable.width, targetHeight / drawable.height)
        : Math.min(targetWidth / drawable.width, targetHeight / drawable.height);
    const drawWidth = Math.max(1, Math.round(drawable.width * scale));
    const drawHeight = Math.max(1, Math.round(drawable.height * scale));
    const drawX = Math.round((targetWidth - drawWidth) / 2);
    const drawY = Math.round((targetHeight - drawHeight) / 2);

    context.drawImage(drawable.source, drawX, drawY, drawWidth, drawHeight);
    drawWatermark(context, targetWidth, targetHeight, watermarkKind, watermarkText, logoDrawable);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(
        (nextBlob) => resolve(nextBlob),
        selectedOutput.mimeType,
        selectedOutput.qualityEnabled ? quality : undefined,
      );
    });

    if (!blob) {
      throw new Error('This file could not be exported in the selected format.');
    }

    return {
      ...item,
      status: 'done' as const,
      outputBlob: blob,
      outputName: buildOutputName(renamePattern, item, index, preset, selectedOutput.extension),
      outputSize: blob.size,
      error: null,
    };
  } catch (error) {
    return {
      ...item,
      status: 'failed' as const,
      outputBlob: null,
      outputName: null,
      outputSize: null,
      error: error instanceof Error ? error.message : 'This file could not be processed.',
    };
  } finally {
    drawable.dispose();
  }
}

export default function BrowserBatchListingAssetOptimizer({
  variant,
}: BrowserBatchListingAssetOptimizerProps) {
  const initialPreset =
    workflowPresets.find((preset) => preset.id === variant?.defaultWorkflowId) ??
    workflowPresets[0];
  const inputRef = useRef<HTMLInputElement | null>(null);
  const logoInputRef = useRef<HTMLInputElement | null>(null);
  const itemsRef = useRef<QueueItem[]>([]);
  const [items, setItems] = useState<QueueItem[]>([]);
  const [selectedPreviewId, setSelectedPreviewId] = useState<string | null>(null);
  const [activeWorkflowId, setActiveWorkflowId] = useState(initialPreset.id);
  const [targetWidth, setTargetWidth] = useState(initialPreset.width);
  const [targetHeight, setTargetHeight] = useState(initialPreset.height);
  const [frameMode, setFrameMode] = useState<FrameMode>(initialPreset.frameMode);
  const [backgroundPreset, setBackgroundPreset] = useState<BackgroundPreset>(
    initialPreset.backgroundPreset,
  );
  const [outputFormat, setOutputFormat] = useState<OutputFormat>(initialPreset.outputFormat);
  const [quality, setQuality] = useState(initialPreset.quality);
  const [watermarkKind, setWatermarkKind] = useState<WatermarkKind>(initialPreset.watermarkKind);
  const [watermarkText, setWatermarkText] = useState(initialPreset.watermarkText);
  const [watermarkLogo, setWatermarkLogo] = useState<File | null>(null);
  const [renamePattern, setRenamePattern] = useState(initialPreset.renamePattern);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isPreparingQueue, setIsPreparingQueue] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPreparingZip, setIsPreparingZip] = useState(false);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [selectedOutputPreviewUrl, setSelectedOutputPreviewUrl] = useState<string | null>(null);

  const activeWorkflow = workflowPresets.find((preset) => preset.id === activeWorkflowId) ?? null;
  const selectedOutput = useMemo(
    () => outputOptions.find((option) => option.value === outputFormat) ?? outputOptions[0],
    [outputFormat],
  );
  const selectedPreviewItem =
    items.find((item) => item.id === selectedPreviewId) ?? items[0] ?? null;
  const queuedCount = items.filter((item) => item.status === 'queued').length;
  const processingCount = items.filter((item) => item.status === 'processing').length;
  const doneCount = items.filter((item) => item.status === 'done').length;
  const failedCount = items.filter((item) => item.status === 'failed').length;
  const totalSourceSize = items.reduce((sum, item) => sum + item.size, 0);
  const totalOutputSize = items.reduce((sum, item) => sum + (item.outputSize ?? 0), 0);
  const hasProcessedBatch = doneCount > 0 || failedCount > 0;
  const progressPercent = items.length
    ? Math.round(((doneCount + failedCount) / items.length) * 100)
    : 0;
  const outputDescriptor = `${selectedOutput.label} • ${targetWidth} × ${targetHeight}`;
  const selectedPreviewApplied = [
    outputDescriptor,
    frameMode === 'fit' ? 'Fit framing' : 'Fill framing',
    backgroundPresets.find((preset) => preset.value === backgroundPreset)?.label ?? 'Background',
    selectedOutput.qualityEnabled ? `${Math.round(quality * 100)}% compression` : 'Lossless PNG',
    watermarkKind === 'off'
      ? 'No watermark'
      : watermarkKind === 'logo'
        ? 'Logo watermark'
        : `Text watermark${watermarkText.trim() ? ` · ${watermarkText.trim()}` : ''}`,
    renamePattern || '{name}-{preset}-{index}',
  ];

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => {
    return () => {
      itemsRef.current.forEach((item) => {
        URL.revokeObjectURL(item.sourceUrl);
      });
    };
  }, []);

  useEffect(() => {
    if (!selectedPreviewItem?.outputBlob) {
      setSelectedOutputPreviewUrl((current) => {
        if (current) {
          URL.revokeObjectURL(current);
        }
        return null;
      });
      return;
    }

    const nextUrl = URL.createObjectURL(selectedPreviewItem.outputBlob);
    setSelectedOutputPreviewUrl((current) => {
      if (current) {
        URL.revokeObjectURL(current);
      }
      return nextUrl;
    });

    return () => URL.revokeObjectURL(nextUrl);
  }, [selectedPreviewItem]);

  function invalidateProcessedResults() {
    setItems((currentItems) =>
      currentItems.map((item) => ({
        ...item,
        status: 'queued',
        outputBlob: null,
        outputName: null,
        outputSize: null,
        error: null,
      })),
    );
  }

  function markWorkflowCustom() {
    if (activeWorkflowId !== 'custom') {
      setActiveWorkflowId('custom');
    }
  }

  function applyWorkflowPreset(preset: WorkflowPreset) {
    setActiveWorkflowId(preset.id);
    setTargetWidth(preset.width);
    setTargetHeight(preset.height);
    setFrameMode(preset.frameMode);
    setBackgroundPreset(preset.backgroundPreset);
    setOutputFormat(preset.outputFormat);
    setQuality(preset.quality);
    setWatermarkKind(preset.watermarkKind);
    setWatermarkText(preset.watermarkText);
    setRenamePattern(preset.renamePattern);
    setErrorMessage(null);
    setCreditMessage(null);
    invalidateProcessedResults();
  }

  async function handleIncomingFiles(fileList: FileList | File[] | null) {
    setErrorMessage(null);
    setCreditMessage(null);

    if (!fileList?.length || isProcessing) {
      return;
    }

    const nextFiles = Array.from(fileList);
    const remainingSlots = Math.max(0, maxBatchFiles - items.length);
    const validFiles = nextFiles
      .slice(0, remainingSlots)
      .filter((file) => supportedTypes.has(file.type) && file.size <= maxFileSize);

    if (!validFiles.length) {
      setErrorMessage(
        `Choose PNG, JPG, JPEG, WebP, GIF, or BMP files up to ${Math.round(maxFileSize / (1024 * 1024))} MB each. The queue supports up to ${maxBatchFiles} files.`,
      );
      return;
    }

    if (validFiles.length < nextFiles.length) {
      setErrorMessage(
        `Some files were skipped because the queue supports up to ${maxBatchFiles} browser-friendly images and ${Math.round(maxFileSize / (1024 * 1024))} MB per file.`,
      );
    }

    setIsPreparingQueue(true);

    try {
      const preparedItems: QueueItem[] = [];

      for (const file of validFiles) {
        preparedItems.push(await prepareQueueItem(file));
        await yieldToBrowser();
      }

      setItems((currentItems) => [...currentItems, ...preparedItems]);
      setSelectedPreviewId((current) => current ?? preparedItems[0]?.id ?? null);
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'The queue could not be prepared locally.',
      );
    } finally {
      setIsPreparingQueue(false);
    }
  }

  function removeItem(itemId: string) {
    if (isProcessing) {
      return;
    }

    setItems((currentItems) => {
      const target = currentItems.find((item) => item.id === itemId);

      if (target) {
        URL.revokeObjectURL(target.sourceUrl);
      }

      const nextItems = currentItems.filter((item) => item.id !== itemId);

      setSelectedPreviewId((current) => {
        if (current !== itemId) {
          return current;
        }

        return nextItems[0]?.id ?? null;
      });

      return nextItems;
    });
  }

  function clearAll() {
    if (isProcessing) {
      return;
    }

    items.forEach((item) => {
      URL.revokeObjectURL(item.sourceUrl);
    });
    setItems([]);
    setSelectedPreviewId(null);
    setErrorMessage(null);
    setCreditMessage(null);
  }

  async function authorizeUsage(units: number) {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    const idToken = await getToolAuthToken();

    if (idToken) {
      headers.authorization = `Bearer ${idToken}`;
    }

    const usageResponse = await fetch('/api/tools/usage', {
      method: 'POST',
      headers,
      body: JSON.stringify({
        toolSlug: variant?.toolSlug ?? 'batch-listing-asset-optimizer',
        units,
      }),
    });
    const usagePayload = (await usageResponse.json().catch(() => ({}))) as {
      success?: boolean;
      data?: {
        credits?: {
          plan?: 'guest' | 'free' | 'pro';
          remaining?: number | null;
          windowLabel?: string;
        };
      };
      error?: unknown;
    };

    if (!usageResponse.ok || !usagePayload.success) {
      const message =
        typeof usagePayload.error === 'object' &&
        usagePayload.error &&
        'message' in usagePayload.error
          ? String((usagePayload.error as { message?: unknown }).message)
          : typeof usagePayload.error === 'string'
            ? usagePayload.error
            : 'Credits are not available for another batch run right now.';
      throw new Error(message);
    }

    return usagePayload;
  }

  async function runBatchProcessing() {
    if (!items.length) {
      setErrorMessage('Add a batch of listing images first.');
      return;
    }

    if (watermarkKind === 'logo' && !watermarkLogo) {
      setErrorMessage('Choose a watermark logo first, or switch watermarking off.');
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);
    setCreditMessage(null);
    invalidateProcessedResults();

    try {
      const usagePayload = await authorizeUsage(items.length);
      const workflowPreset =
        workflowPresets.find((preset) => preset.id === activeWorkflowId) ?? activeWorkflow;
      const logoDrawable =
        watermarkKind === 'logo' && watermarkLogo
          ? await loadDrawableFromFile(watermarkLogo)
          : null;

      try {
        for (let index = 0; index < items.length; index += 1) {
          const item = items[index]!;

          setItems((currentItems) =>
            currentItems.map((currentItem) =>
              currentItem.id === item.id
                ? {
                    ...currentItem,
                    status: 'processing',
                    error: null,
                  }
                : currentItem,
            ),
          );

          await yieldToBrowser();

          const result = await processQueueItem(
            item,
            index,
            workflowPreset,
            targetWidth,
            targetHeight,
            frameMode,
            backgroundPreset,
            outputFormat,
            quality,
            watermarkKind,
            watermarkText,
            logoDrawable,
            renamePattern,
          );

          setItems((currentItems) =>
            currentItems.map((currentItem) => (currentItem.id === item.id ? result : currentItem)),
          );
        }
      } finally {
        logoDrawable?.dispose();
      }

      setCreditMessage(
        usagePayload.data?.credits?.plan === 'pro'
          ? 'Batch finished locally. Files are ready for ZIP export.'
          : usagePayload.data?.credits?.remaining !== undefined
            ? `Batch finished locally. ${usagePayload.data.credits.remaining} credits left in this ${usagePayload.data.credits.windowLabel?.toLowerCase() || 'current'} window.`
            : 'Batch finished locally. Files are ready for ZIP export.',
      );
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'The batch run could not finish.');
    } finally {
      setIsProcessing(false);
    }
  }

  async function retryItem(itemId: string) {
    if (isProcessing) {
      return;
    }

    const item = items.find((candidate) => candidate.id === itemId);

    if (!item) {
      return;
    }

    if (watermarkKind === 'logo' && !watermarkLogo) {
      setErrorMessage('Choose a watermark logo first, or switch watermarking off.');
      return;
    }

    setErrorMessage(null);
    setSelectedPreviewId(itemId);

    setItems((currentItems) =>
      currentItems.map((currentItem) =>
        currentItem.id === itemId
          ? {
              ...currentItem,
              status: 'processing',
              outputBlob: null,
              outputName: null,
              outputSize: null,
              error: null,
            }
          : currentItem,
      ),
    );

    const workflowPreset =
      workflowPresets.find((preset) => preset.id === activeWorkflowId) ?? activeWorkflow;
    const logoDrawable =
      watermarkKind === 'logo' && watermarkLogo ? await loadDrawableFromFile(watermarkLogo) : null;

    try {
      const retryIndex = items.findIndex((candidate) => candidate.id === itemId);
      const result = await processQueueItem(
        item,
        Math.max(retryIndex, 0),
        workflowPreset,
        targetWidth,
        targetHeight,
        frameMode,
        backgroundPreset,
        outputFormat,
        quality,
        watermarkKind,
        watermarkText,
        logoDrawable,
        renamePattern,
      );

      setItems((currentItems) =>
        currentItems.map((currentItem) => (currentItem.id === itemId ? result : currentItem)),
      );
    } catch (error) {
      setItems((currentItems) =>
        currentItems.map((currentItem) =>
          currentItem.id === itemId
            ? {
                ...currentItem,
                status: 'failed',
                outputBlob: null,
                outputName: null,
                outputSize: null,
                error: error instanceof Error ? error.message : 'This file could not be processed.',
              }
            : currentItem,
        ),
      );
    } finally {
      logoDrawable?.dispose();
    }
  }

  async function downloadZip() {
    const readyItems = items.filter((item) => item.outputBlob && item.outputName);

    if (!readyItems.length) {
      return;
    }

    setIsPreparingZip(true);

    try {
      const zip = new JSZip();
      readyItems.forEach((item) => {
        if (item.outputBlob && item.outputName) {
          zip.file(item.outputName, item.outputBlob);
        }
      });

      const zipBlob = await zip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 },
      });
      const zipUrl = URL.createObjectURL(zipBlob);
      const anchor = document.createElement('a');
      anchor.href = zipUrl;
      anchor.download = 'cleanor-listing-assets.zip';
      anchor.click();
      window.setTimeout(() => URL.revokeObjectURL(zipUrl), 4000);
    } finally {
      setIsPreparingZip(false);
    }
  }

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>Prepare listing-ready asset packs for marketplace uploads in one local batch</h2>
          <p>
            Upload a queue of product images, choose one export profile, process the batch locally,
            and download a ZIP pack that is ready for catalogs, storefronts, and listing uploads.
          </p>
        </div>
        <span className="tool-live-badge">{variant?.badgeLabel ?? 'Local browser processing'}</span>
      </div>

      <div className="tool-product-prep-steps" aria-label="Workflow steps">
        <div className="tool-product-prep-step tool-product-prep-step-complete">
          <strong>1. Upload</strong>
          <span>Add 10 to 100 listing images</span>
        </div>
        <div
          className={`tool-product-prep-step ${
            items.length ? 'tool-product-prep-step-active' : ''
          }`}
        >
          <strong>2. Configure</strong>
          <span>Choose one export profile</span>
        </div>
        <div
          className={`tool-product-prep-step ${
            items.length && !hasProcessedBatch ? 'tool-product-prep-step-active' : ''
          } ${hasProcessedBatch ? 'tool-product-prep-step-complete' : ''}`}
        >
          <strong>3. Process</strong>
          <span>Run the local queue</span>
        </div>
        <div
          className={`tool-product-prep-step ${hasProcessedBatch ? 'tool-product-prep-step-active' : ''}`}
        >
          <strong>4. Export</strong>
          <span>Download one ZIP pack</span>
        </div>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <div className="tool-product-prep-section">
            <div className="tool-product-prep-section-heading">
              <strong>Upload</strong>
              <span>Start with the full listing queue and keep the batch on this device.</span>
            </div>
            <button
              className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
              disabled={isProcessing}
              onClick={() => inputRef.current?.click()}
              onDragEnter={() => setIsDragActive(true)}
              onDragLeave={() => setIsDragActive(false)}
              onDragOver={(event) => {
                event.preventDefault();
                setIsDragActive(true);
              }}
              onDrop={(event) => {
                event.preventDefault();
                setIsDragActive(false);
                void handleIncomingFiles(event.dataTransfer.files);
              }}
              type="button"
            >
              <div>
                <strong>
                  {isPreparingQueue
                    ? 'Preparing queue locally…'
                    : items.length
                      ? `${items.length} file${items.length === 1 ? '' : 's'} in queue`
                      : 'Upload listing images or drop them here'}
                </strong>
                <span>
                  {items.length
                    ? `${formatFileSize(totalSourceSize)} total • PNG, JPG, WebP, GIF, BMP`
                    : `Up to ${maxBatchFiles} files • ${Math.round(maxFileSize / (1024 * 1024))} MB each • local browser processing`}
                </span>
              </div>
            </button>
            <input
              ref={inputRef}
              accept="image/png,image/jpeg,image/webp,image/gif,image/bmp"
              className="tool-live-file-input"
              multiple
              onChange={(event) => {
                void handleIncomingFiles(event.target.files);
                event.currentTarget.value = '';
              }}
              type="file"
            />
          </div>

          <div className="tool-product-prep-section">
            <div className="tool-product-prep-section-heading">
              <strong>Configure</strong>
              <span>
                Choose one output profile for the whole batch, then only adjust the essentials.
              </span>
            </div>

            <div className="tool-batch-preset-grid" aria-label="Batch export presets">
              {workflowPresets.map((preset) => (
                <button
                  key={preset.id}
                  className={`tool-batch-preset-card ${
                    activeWorkflowId === preset.id ? 'tool-batch-preset-card-active' : ''
                  }`}
                  disabled={isProcessing}
                  onClick={() => applyWorkflowPreset(preset)}
                  type="button"
                >
                  <strong>{preset.label}</strong>
                  <span>{`${preset.width} × ${preset.height}`}</span>
                  <small>
                    {`${outputOptions.find((option) => option.value === preset.outputFormat)?.label ?? 'PNG'} • ${preset.frameMode === 'fit' ? 'fit' : 'fill'} • ${backgroundPresets.find((option) => option.value === preset.backgroundPreset)?.label ?? 'white'}`}
                  </small>
                </button>
              ))}
            </div>

            <div className="tool-batch-preset-summary">
              <strong>{activeWorkflow?.label ?? 'Custom profile'}</strong>
              <span>{(activeWorkflow ?? initialPreset).summary}</span>
            </div>

            <div className="tool-live-controls">
              <label className="field">
                <span>Format</span>
                <select
                  disabled={isProcessing}
                  onChange={(event) => {
                    markWorkflowCustom();
                    setOutputFormat(event.target.value as OutputFormat);
                    invalidateProcessedResults();
                  }}
                  value={outputFormat}
                >
                  {outputOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>Canvas size</span>
                <div className="tool-batch-size-fields">
                  <input
                    disabled={isProcessing}
                    min="800"
                    onChange={(event) => {
                      markWorkflowCustom();
                      setTargetWidth(Math.max(800, Number(event.target.value) || 800));
                      invalidateProcessedResults();
                    }}
                    type="number"
                    value={targetWidth}
                  />
                  <span>×</span>
                  <input
                    disabled={isProcessing}
                    min="800"
                    onChange={(event) => {
                      markWorkflowCustom();
                      setTargetHeight(Math.max(800, Number(event.target.value) || 800));
                      invalidateProcessedResults();
                    }}
                    type="number"
                    value={targetHeight}
                  />
                </div>
              </label>
              <label className="field">
                <span>Background</span>
                <select
                  disabled={isProcessing}
                  onChange={(event) => {
                    markWorkflowCustom();
                    setBackgroundPreset(event.target.value as BackgroundPreset);
                    invalidateProcessedResults();
                  }}
                  value={backgroundPreset}
                >
                  {backgroundPresets.map((preset) => (
                    <option key={preset.value} value={preset.value}>
                      {preset.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>Framing</span>
                <select
                  disabled={isProcessing}
                  onChange={(event) => {
                    markWorkflowCustom();
                    setFrameMode(event.target.value as FrameMode);
                    invalidateProcessedResults();
                  }}
                  value={frameMode}
                >
                  {frameModeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="tool-product-prep-advanced">
              <button
                className="button button-ghost tool-product-prep-advanced-toggle"
                onClick={() => setIsAdvancedOpen((current) => !current)}
                type="button"
              >
                {isAdvancedOpen ? 'Hide advanced' : 'Advanced'}
              </button>

              {isAdvancedOpen ? (
                <div className="tool-product-prep-advanced-panel">
                  <div className="tool-live-controls">
                    <label
                      className={`field tool-live-quality ${
                        selectedOutput.qualityEnabled ? '' : 'tool-live-quality-disabled'
                      }`}
                    >
                      <span>Compression</span>
                      <input
                        disabled={isProcessing || !selectedOutput.qualityEnabled}
                        max="0.98"
                        min="0.45"
                        onChange={(event) => {
                          markWorkflowCustom();
                          setQuality(Number(event.target.value));
                          invalidateProcessedResults();
                        }}
                        step="0.01"
                        type="range"
                        value={quality}
                      />
                      <small>
                        {selectedOutput.qualityEnabled
                          ? `${Math.round(quality * 100)}% quality`
                          : 'PNG keeps full detail without a compression slider.'}
                      </small>
                    </label>
                    <label className="field">
                      <span>Watermark</span>
                      <select
                        disabled={isProcessing}
                        onChange={(event) => {
                          markWorkflowCustom();
                          setWatermarkKind(event.target.value as WatermarkKind);
                          invalidateProcessedResults();
                        }}
                        value={watermarkKind}
                      >
                        <option value="off">Off</option>
                        <option value="text">Text</option>
                        <option value="logo">Logo</option>
                      </select>
                    </label>
                    <label className="field">
                      <span>Rename pattern</span>
                      <input
                        disabled={isProcessing}
                        onChange={(event) => {
                          markWorkflowCustom();
                          setRenamePattern(event.target.value);
                          invalidateProcessedResults();
                        }}
                        placeholder="{name}-{preset}-{index}"
                        type="text"
                        value={renamePattern}
                      />
                      <small>
                        {'{name}'} {'{preset}'} {'{index}'}
                      </small>
                    </label>
                  </div>

                  {watermarkKind !== 'off' ? (
                    <div className="tool-live-controls">
                      {watermarkKind === 'logo' ? (
                        <label className="field">
                          <span>Logo file</span>
                          <button
                            className="button button-ghost tool-product-prep-logo-picker"
                            disabled={isProcessing}
                            onClick={() => logoInputRef.current?.click()}
                            type="button"
                          >
                            {watermarkLogo ? watermarkLogo.name : 'Choose logo'}
                          </button>
                          <input
                            ref={logoInputRef}
                            accept="image/*"
                            className="tool-live-file-input"
                            disabled={isProcessing}
                            onChange={(event) => {
                              markWorkflowCustom();
                              setWatermarkLogo(event.target.files?.[0] ?? null);
                              invalidateProcessedResults();
                              event.currentTarget.value = '';
                            }}
                            type="file"
                          />
                        </label>
                      ) : (
                        <label className="field">
                          <span>Watermark text</span>
                          <input
                            disabled={isProcessing}
                            onChange={(event) => {
                              markWorkflowCustom();
                              setWatermarkText(event.target.value);
                              invalidateProcessedResults();
                            }}
                            placeholder="Brand"
                            type="text"
                            value={watermarkText}
                          />
                        </label>
                      )}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>

          <div className="tool-product-prep-section">
            <div className="tool-product-prep-section-heading">
              <strong>Process</strong>
              <span>
                Run one local pass, then review the queue status before exporting the pack.
              </span>
            </div>

            <div className="tool-batch-queue-summary">
              <div className="tool-batch-progress" aria-label="Queue progress">
                <div className="tool-batch-progress-bar">
                  <span style={{ width: `${progressPercent}%` }} />
                </div>
                <div className="tool-batch-progress-meta">
                  <strong>
                    {items.length ? `${progressPercent}% complete` : 'Queue is empty'}
                  </strong>
                  <span>
                    {items.length
                      ? `${doneCount} ready • ${failedCount} failed • ${queuedCount + processingCount} remaining`
                      : 'Add files, then process the batch locally.'}
                  </span>
                </div>
              </div>
              {items.length ? (
                <div className="tool-batch-queue-strip" aria-label="Queue status snapshot">
                  <span>{`${items.length} queued`}</span>
                  <span>{`${doneCount} ready`}</span>
                  <span>{`${failedCount} failed`}</span>
                  <span>{`${queuedCount + processingCount} left`}</span>
                </div>
              ) : null}
              <div className="hero-actions tool-live-actions">
                {!hasProcessedBatch ? (
                  <button
                    className="button"
                    disabled={!items.length || isProcessing || isPreparingQueue}
                    onClick={() => void runBatchProcessing()}
                    type="button"
                  >
                    {isProcessing
                      ? 'Processing locally…'
                      : `Process ${items.length ? `${items.length} files` : 'batch'}`}
                  </button>
                ) : (
                  <div className="tool-batch-stage-note">
                    <strong>Batch processed</strong>
                    <span>Use Export for the ZIP pack, or re-run after changing the profile.</span>
                  </div>
                )}
                <button
                  className="button button-ghost"
                  disabled={!items.length || isProcessing}
                  onClick={clearAll}
                  type="button"
                >
                  Clear all
                </button>
              </div>
            </div>

            {items.length ? (
              <div className="tool-batch-list">
                {items.map((item, index) => (
                  <article className="tool-batch-item" key={item.id}>
                    <div className="tool-batch-item-top">
                      <strong>{item.name}</strong>
                      <span className={`tool-batch-status tool-batch-status-${item.status}`}>
                        {getProgressLabel(item.status)}
                      </span>
                    </div>
                    <div className="tool-batch-item-meta">
                      <span>{`${normalizeSourceLabel(item.type)} • ${item.width} × ${item.height}`}</span>
                      <span>{formatFileSize(item.size)}</span>
                      {item.outputSize ? <span>{formatFileSize(item.outputSize)}</span> : null}
                    </div>
                    <div className="tool-batch-item-actions">
                      <button
                        className={`tool-batch-link ${
                          selectedPreviewItem?.id === item.id ? 'tool-batch-link-active' : ''
                        }`}
                        onClick={() => setSelectedPreviewId(item.id)}
                        type="button"
                      >
                        Preview
                      </button>
                      {item.status === 'failed' ? (
                        <button
                          className="tool-batch-link"
                          onClick={() => void retryItem(item.id)}
                          type="button"
                        >
                          Retry
                        </button>
                      ) : null}
                      <button
                        className="tool-batch-link"
                        disabled={isProcessing}
                        onClick={() => removeItem(item.id)}
                        type="button"
                      >
                        Remove
                      </button>
                      <span className="tool-batch-muted">
                        {item.error
                          ? item.error
                          : item.outputName
                            ? item.outputName
                            : `Queue item ${index + 1}`}
                      </span>
                    </div>
                  </article>
                ))}
              </div>
            ) : null}
          </div>

          <div className="tool-product-prep-section">
            <div className="tool-product-prep-section-heading">
              <strong>Preview</strong>
              <span>
                Review one sample file before and after processing without leaving the batch flow.
              </span>
            </div>
            {selectedPreviewItem ? (
              <>
                <div className="tool-live-preview tool-compression-preview">
                  <div className="tool-compression-preview-grid">
                    <div className="tool-live-preview-frame">
                      <img
                        alt={`${selectedPreviewItem.name} source preview`}
                        src={selectedPreviewItem.sourceUrl}
                      />
                    </div>
                    <div className="tool-live-preview-frame">
                      {selectedOutputPreviewUrl ? (
                        <img
                          alt={`${selectedPreviewItem.name} processed preview`}
                          src={selectedOutputPreviewUrl}
                        />
                      ) : (
                        <div className="tool-editor-empty">
                          <strong>Ready after processing</strong>
                          <span>
                            Process the batch to preview the listing-ready result for this sample.
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="tool-batch-preview-applied" aria-label="Applied batch settings">
                  {selectedPreviewApplied.map((detail) => (
                    <span key={detail}>{detail}</span>
                  ))}
                </div>
                <p className="tool-batch-preview-meta">
                  {selectedPreviewItem.name}
                  {selectedPreviewItem.outputSize
                    ? ` • ${formatFileSize(selectedPreviewItem.outputSize)}`
                    : ''}
                  {selectedPreviewItem.outputName ? ` • ${selectedPreviewItem.outputName}` : ''}
                </p>
              </>
            ) : (
              <div className="tool-editor-empty">
                <strong>Sample result will appear here</strong>
                <span>
                  Upload a queue and select one item to review before and after processing.
                </span>
              </div>
            )}
          </div>

          <div className="tool-product-prep-section">
            <div className="tool-product-prep-section-heading">
              <strong>Export</strong>
              <span>
                Download one ZIP pack when the ready count matches the queue you want to ship.
              </span>
            </div>
            {doneCount ? (
              <div className="hero-actions tool-live-actions">
                <button
                  className="button"
                  disabled={isPreparingZip || isProcessing}
                  onClick={() => void downloadZip()}
                  type="button"
                >
                  {isPreparingZip ? 'Preparing ZIP pack…' : `Download ZIP pack (${doneCount})`}
                </button>
              </div>
            ) : (
              <div className="tool-batch-stage-note">
                <strong>ZIP export appears here</strong>
                <span>Process the queue first, then download one finished pack.</span>
              </div>
            )}
          </div>

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel tool-product-prep-summary">
          <h3>Summary</h3>
          <div className="tool-live-stats">
            <div>
              <strong>Profile</strong>
              <span>{activeWorkflow?.label ?? 'Custom'}</span>
            </div>
            <div>
              <strong>Output</strong>
              <span>{outputDescriptor}</span>
            </div>
            <div>
              <strong>Format</strong>
              <span>{selectedOutput.label}</span>
            </div>
            <div>
              <strong>Queue</strong>
              <span>{items.length ? `${items.length} files` : 'Add files to start'}</span>
            </div>
            <div>
              <strong>Ready</strong>
              <span>{doneCount ? `${doneCount} ready` : 'Run process first'}</span>
            </div>
            <div>
              <strong>Failed</strong>
              <span>{failedCount ? `${failedCount} need retry` : 'None'}</span>
            </div>
            <div>
              <strong>Watermark</strong>
              <span>
                {watermarkKind === 'off' ? 'Off' : watermarkKind === 'logo' ? 'Logo' : 'Text'}
              </span>
            </div>
            <div>
              <strong>Rename</strong>
              <span>{renamePattern || '{name}-{preset}-{index}'}</span>
            </div>
            <div>
              <strong>ZIP estimate</strong>
              <span>{doneCount ? formatFileSize(totalOutputSize) : 'Built after processing'}</span>
            </div>
          </div>
          <div className="tool-live-panel-note">
            <strong>Local only</strong>
            <span>
              Queue processing and ZIP export stay in the browser. Files are rebuilt through canvas,
              so common metadata is stripped on save.
            </span>
          </div>
        </aside>
      </div>
    </article>
  );
}
